//------------------------------------------------------------------------------
// Node Modules ----------------------------------------------------------------
import React from 'react';
import classNames from 'classnames';
import { Switch, Route, Redirect } from 'react-router-dom';
import { isAndroid, isIOS } from 'react-device-detect';
import PendingModal from './client/pendingModal';
import FocusTrap from 'focus-trap-react';
//------------------------------------------------------------------------------
// Style -----------------------------------------------------------------------
import styles from './index.module.scss';
//------------------------------------------------------------------------------
// My Components ---------------------------------------------------------------
import { Header, Modal, Popover } from '@cmp/common';
import {
  Recommendations,
  Products,
  Product,
  Patient,
  Recommendation,
  Success,
  Media,
  ProfessionalCodeShare,
  ProfessionalCodeSuccess,
} from './client';

import Stripe from './client/stripe/index.jsx';
//------------------------------------------------------------------------------
// Local Storage ---------------------------------------------------------------
import { getValue, storeValue } from '@helpers/localStorage';
import {
  WelcomeStorageKey,
  WelcomeShortcutStorageKey,
  SkipStripe,
  VerifiedStripeKey,
  IsAdminApproved,
  DisplayPendingModal,
  AfterWelcomeModalLastDisplayed,
  AfterWelcomeModalLastDisplayedDontShowAgain,
  QRAccountStatusTrueModalShown,
} from '@helpers/constants/localStorage';
import { DURATION_48_HOURS } from '@helpers/constants/user';
//------------------------------------------------------------------------------
// API -------------------------------------------------------------------------
import { loadProviders } from '@api/endpoints/get/providers';
import { getProviderQRStatus } from '@api/endpoints/get/providers';

//------------------------------------------------------------------------------
// Helpers ---------------------------------------------------------------------
import { withModal } from '@helpers/modal';
import { withTranslation } from 'react-i18next';
import {
  handleAfterWelcomeModalHelper,
  handleAfterWelcomeQuickRecoAccTrueModalHelper,
} from '@helpers/account';
//------------------------------------------------------------------------------
// Contexts ---------------------------------------------------------------------
import { StoreConfigContext } from '@helpers/storeConfigContext';
import ProductProvider from '@context/product/ProductProvider';
import { isSkipStripe, verifiedStripe } from '../../../helpers/account';
//------------------------------------------------------------------------------

const appUrl = window.location.href;

// React Class -----------------------------------------------------------------
class Dashboard extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      toggle: false,
    };
    this.handleToggle = this.handleToggle.bind(this);
    this.mountTrap = this.mountTrap.bind(this);
    this.unmountTrap = this.unmountTrap.bind(this);
  }

  handleToggle() {
    this.setState((state) => ({ toggle: !state.toggle }));
  }

  componentDidMount() {
    this.pushAuxiliarRoutesIfNeeded();
    this.mountTrap();
  }

  mountTrap() {
    this.setState({ activeTrap: true });
  }

  unmountTrap() {
    this.setState({ activeTrap: false });
  }

  pushAuxiliarRoutesIfNeeded() {
    const { history, location, t } = this.props;
    const {
      featureFlags: { useEUQuickReco },
    } = this.context;

    // Recommendations ---------------------------------------------------------

    loadProviders().then(({ data }) => {
      const { hasQrAccount, isApproved } = data;
      let pushWelcomeRoute = false;

      if (isSkipStripe() && !verifiedStripe()) {
        pushWelcomeRoute = this.pushRouteIfNeeded(
          '/welcome',
          WelcomeStorageKey
        );
      }
      if (
        history.location.state &&
        history.location.state.stripeSuccess &&
        verifiedStripe()
      ) {
        pushWelcomeRoute = this.pushRouteForStripeSuccess(
          '/stripe-account-success'
        );
      } else {
        if (
          !!getValue(DisplayPendingModal, true) &&
          !getValue(SkipStripe, true)
        ) {
          storeValue(DisplayPendingModal, '');
          history.push({
            pathname: '/pending',
            state: { from: location, modal: true },
          });
        }
      }

      if (pushWelcomeRoute && (isAndroid || isIOS)) {
        this.pushRouteIfNeeded('/shortcut', WelcomeShortcutStorageKey);
      }

      // Validate if payload for vet is - isApproved and QR account
      // If provider payload hasQrAccount == true, then no need to show /after-welcome route.
      if (useEUQuickReco && isApproved && !hasQrAccount) {
        // Get vet hcode/clinic code/practiceNumber from colgate_user local storage
        let loggedinVetInfo = JSON.parse(localStorage.getItem('colgate_user'));
        localStorage.setItem('has_quick_reco_acc_true_modal_shown', false);

        // Make a call to Quick reco`s /registered_users endpoint to check HVS/MHS approved vet account exists
        getProviderQRStatus(loggedinVetInfo.data.practiceNumber)
          .then(({ data }) => {
            let hasQRAccountResponse = '';
            if (data.status === 200 && data.isRegistered) {
              hasQRAccountResponse = data.isRegistered;

              //showing modal only once for a vet whose Quick reco account already exists
              if (getValue(QRAccountStatusTrueModalShown) == 'false') {
                if (location.pathname === '/') {
                  history.push({
                    pathname: '/after-welcome',
                    state: { from: location, modal: true },
                  });

                  if (hasQRAccountResponse) {
                    handleAfterWelcomeQuickRecoAccTrueModalHelper({
                      hasQRAccountResponse: true,
                    });
                  }
                }
              }
            } else {
              hasQRAccountResponse = false;
            }
          })
          .catch((error) => {
            // Handling the error from the promise rejection
            console.error(
              'Vet/clinic - HCode is not registered with Quick Reco'
            );
          });
      }

      // If call to quick reco api returns hCode not found, then following flow of "remind me later" and "dont show again" continues for isApproved Account
      if (
        useEUQuickReco &&
        isApproved &&
        getValue(QRAccountStatusTrueModalShown) == 'false'
      ) {
        const isDontShowAgain = localStorage.getItem(
          AfterWelcomeModalLastDisplayedDontShowAgain
        );

        const lastDisplayed = localStorage.getItem(
          AfterWelcomeModalLastDisplayed
        );

        // null means user's session hasn't seen the modal yet
        if (lastDisplayed === null && isDontShowAgain === 'false') {
          handleAfterWelcomeModalHelper({ remindMeLater: true });
        }

        if (isDontShowAgain) {
          handleAfterWelcomeModalHelper({ dontShowAgain: true });
        }

        if (!isDontShowAgain) {
          const lastDisplayedTimestamp = Number(lastDisplayed);
          const isValidTimestamp =
            lastDisplayedTimestamp ===
            new Date(lastDisplayedTimestamp).getTime();

          const durationBetweenModalDisplayTimes = Date.now() - lastDisplayed;
          let durationToUse = DURATION_48_HOURS;
          if (parseInt(import.meta.env.VITE_WELCOME_MODAL_DURATION)) {
            durationToUse = parseInt(
              import.meta.env.VITE_WELCOME_MODAL_DURATION
            );
          }
          const canDisplayModal =
            durationBetweenModalDisplayTimes > durationToUse;

          if (
            isValidTimestamp &&
            canDisplayModal &&
            location.pathname === '/'
          ) {
            history.push({
              pathname: '/after-welcome',
              state: { from: location, modal: true },
            });
          }
        }
      }
    });
  }

  pushRouteForStripeSuccess(pathname) {
    const { history, location } = this.props;
    history.push({
      pathname,
      state: { from: location, modal: true },
    });
    return true;
  }

  pushRouteIfNeeded(pathname, key) {
    const { history, location } = this.props;
    const shownRoute = getValue(key);

    if (!shownRoute) {
      history.push({
        pathname,
        state: { from: location, modal: true },
      });
      storeValue(key, true);
    }
    return shownRoute;
  }

  render() {
    const { from, isModal, t } = this.props;
    const componentClasses = classNames(styles.Dashboard);
    const { toggle } = this.state;
    const { handleToggle } = this;

    return (
      <ProductProvider>
        <div className={componentClasses}>
          <Header toggle={toggle} onToggle={handleToggle} />
          <div className={styles.Content}>
            <Switch>
              {isModal && (
                <Route exact path="/recommendation/stripe" component={Stripe} />
              )}
              {isModal && (
                <Route
                  exact
                  path="/recommendation/patient"
                  component={Patient}
                />
              )}
              {isModal && (
                <Route
                  exact
                  path="/recommendation/success"
                  component={Success}
                />
              )}
              {isModal && (
                <Route
                  exact
                  path="/recommendation/professional-code-share"
                  component={ProfessionalCodeShare}
                />
              )}
              {isModal && (
                <Route
                  exact
                  path="/recommendation/professional-code-success"
                  component={ProfessionalCodeSuccess}
                />
              )}
              {isModal && (
                <Route
                  path="/recommendation/:id"
                  render={(props) => (
                    <Recommendation isModal={isModal} {...props} />
                  )}
                />
              )}

              {isModal && getValue(SkipStripe) === 'true' && (
                <Route
                  exact
                  path="/welcome"
                  render={(props) => (
                    <FocusTrap>
                      <div>
                        <Modal.Generic
                          title={t('dashboard_WelcomeTo')}
                          subtitle={
                            <div>{t('dashboard_NeedStripeAccount')} </div>
                          }
                          to={'/'}
                          onClick={this.to}
                          action={t('dashboard_Continue')}
                          {...props}
                        />
                      </div>
                    </FocusTrap>
                  )}
                />
              )}
              {isModal && !!getValue(VerifiedStripeKey, true) && (
                <Route
                  exact
                  path="/stripe-account-success"
                  render={() => (
                    <PendingModal
                      history={this.props.history}
                      location={this.props.location}
                      title={t('profile_PendingApprovalHeading')}
                    />
                  )}
                />
              )}
              {isModal && !!!getValue(IsAdminApproved, true) && (
                <Route
                  exact
                  path="/pending"
                  render={() => (
                    <PendingModal
                      history={this.props.history}
                      location={this.props.location}
                      title={t('profile_PendingApprovalHeadingSubsequent')}
                    />
                  )}
                />
              )}
              {isModal && (
                <Route
                  exact
                  path="/shortcut"
                  render={(props) => (
                    <Popover.Generic
                      title={t('dashboard_AddShortcut')}
                      subtitle={
                        <div>
                          {t('dashboard_AddShortcutSubtitle')}{' '}
                          {t('dashboard_AddShortcutSubtitle_to_learn_more')}
                        </div>
                      }
                      auxiliar={t('dashboard_AddShortcutAux')}
                      {...props}
                    />
                  )}
                />
              )}
              {isModal && <Route exact path="/media" component={Media} />}
            </Switch>
            <Switch location={from}>
              {/* Recommendations */}
              <Route
                exact
                path="/recommendations"
                render={(props) => (
                  <Recommendations
                    {...props}
                    fullScreen={(fullScreen) => this.setState({ fullScreen })}
                  />
                )}
              />

              {/* Products */}
              <Route path="/product/:id" component={Product} />
              <Route exact path="/" component={Products} />

              {/* Generic Redirect */}
              <Redirect from="*" to="/" />
            </Switch>
          </div>
        </div>
      </ProductProvider>
    );
  }
}
//------------------------------------------------------------------------------
// Export ----------------------------------------------------------------------
export default withTranslation()(withModal(Dashboard));
Dashboard.contextType = StoreConfigContext;
